import type { UsageTimelineLedger, UsageTimelineEntry, WorkflowSummaryRow, StageEntry } from '../types/admin';
import { normalizeUsageTimelineDurationEvidence, hasCanonicalTimelineDurationEvidence } from './usageTimelineDurationEvidence';


const trustedUsageTimelineLedgers = new WeakSet<object>();

export function isUsableTimelineEnvelope(ledger: UsageTimelineLedger, start: string, end: string): boolean {
  if (
    !ledger || !ledger.coverage || !Array.isArray(ledger.entries) || !Array.isArray(ledger.actors)
    || ledger.timeline_version !== 'timeline-v1'
    || ledger.timeline_display_only !== true
    || !isValidDateStr(ledger.date_range.start)
    || !isValidDateStr(ledger.date_range.end)
    || ledger.date_range.start !== start || ledger.date_range.end !== end
    || ledger.coverage.boundary !== 'inclusive-utc'
    || !isValidDateStr(ledger.coverage.start)
    || !isValidDateStr(ledger.coverage.end)
    || ledger.coverage.start > ledger.coverage.end
    || ledger.coverage.start > start || ledger.coverage.end < end
    || !ledger.coverage.generation_revision || !ledger.coverage.scope_revision || !ledger.coverage.dependency_revision
    || !Number.isSafeInteger(ledger.coverage.entry_limit)
    || !Number.isSafeInteger(ledger.coverage.payload_limit_bytes)
    // Keep this aligned with the server's advertised transport contract.
    // The bound limits received entries; it must not reject a valid empty
    // Today ledger merely because the server permits up to 200k entries.
    || ledger.coverage.entry_limit < 0 || ledger.coverage.entry_limit > 200_000
    || ledger.coverage.payload_limit_bytes < 0 || ledger.coverage.payload_limit_bytes > 64 * 1024 * 1024
    || ledger.entries.length > ledger.coverage.entry_limit
    || ledger.actors.length > 100_000
    || ledger.actors.some((actor: unknown) => {
      const candidate = actor as { email?: unknown; unit?: unknown } | null;
      return !candidate || typeof candidate.email !== 'string' || candidate.email.length === 0
        || candidate.email.length > 512 || (candidate.unit !== null && typeof candidate.unit !== 'string');
    })
  ) return false;
  if (!normalizeUsageTimelineDurationEvidence(ledger)) return false;
  if (trustedUsageTimelineLedgers.has(ledger)) return true;
  let previousTimestamp = '';
  let previousSequence = -1;
  const ids = new Set<string>();
  for (const entry of ledger.entries) {
    // Validate the complete display shape at the transport boundary. The
    // ledger is later cached and rendered without defensive optional chaining,
    // so an unbounded/malformed DTO must fail closed before it reaches L1.
    if (
      !entry || typeof entry !== 'object'
      || typeof entry.entry_id !== 'string' || entry.entry_id.length === 0 || entry.entry_id.length > 512
      || ids.has(entry.entry_id)
      || typeof entry.inclusion_timestamp !== 'string' || !entry.inclusion_timestamp.endsWith('Z')
      || !Number.isSafeInteger(entry.sequence)
      || !entry.workflow || typeof entry.workflow !== 'object'
      || typeof entry.workflow.flow_id !== 'string' || entry.workflow.flow_id.length > 512
      || (entry.workflow.source_flow_id !== undefined && typeof entry.workflow.source_flow_id !== 'string')
      || (entry.workflow.workflow_type !== undefined && entry.workflow.workflow_type !== null
        && typeof entry.workflow.workflow_type !== 'string')
      || (entry.workflow.flow_name !== undefined && typeof entry.workflow.flow_name !== 'string')
      || (entry.workflow.unit !== undefined && entry.workflow.unit !== null && typeof entry.workflow.unit !== 'string')
      || (entry.workflow.status !== undefined && entry.workflow.status !== 'completed' && entry.workflow.status !== 'in_progress')
      || (entry.workflow.active_ms !== undefined && entry.workflow.active_ms !== null
        && (!Number.isFinite(entry.workflow.active_ms) || entry.workflow.active_ms < 0))
      || !Array.isArray(entry.workflow.users) || entry.workflow.users.length > 512
      || entry.workflow.users.some((email: unknown) => typeof email !== 'string' || email.length === 0 || email.length > 512)
      || (!hasCanonicalTimelineDurationEvidence(entry.workflow) && entry.workflow.active_ms_by_day_and_user !== undefined && (
        entry.workflow.active_ms_by_day_and_user === null
        || typeof entry.workflow.active_ms_by_day_and_user !== 'object'
        || Array.isArray(entry.workflow.active_ms_by_day_and_user)
        || Object.entries(entry.workflow.active_ms_by_day_and_user).length > 31
        || Object.entries(entry.workflow.active_ms_by_day_and_user).some(([day, actors]) =>
          !isValidDateStr(day) || day < ledger.coverage.start || day > ledger.coverage.end
          || actors === null || typeof actors !== 'object' || Array.isArray(actors)
          || Object.keys(actors).length > 512
          || Object.entries(actors).some(([email, duration]) =>
            !entry.workflow.users.includes(email) || typeof duration !== 'number'
            || !Number.isFinite(duration) || duration < 0))
      ))
      || (!hasCanonicalTimelineDurationEvidence(entry.workflow) && entry.workflow.user_last_active !== undefined && (
        entry.workflow.user_last_active === null || typeof entry.workflow.user_last_active !== 'object'
        || Array.isArray(entry.workflow.user_last_active)
        || Object.entries(entry.workflow.user_last_active).some(([email, timestamp]) =>
          !entry.workflow.users.includes(email) || typeof timestamp !== 'string'
          || !timestamp.endsWith('Z') || !Number.isFinite(Date.parse(timestamp))
          || timestamp.slice(0, 10) < ledger.coverage.start || timestamp.slice(0, 10) > ledger.coverage.end)
      ))
      || (entry.kind !== 'stage' && entry.kind !== 'action')
      || (entry.kind === 'stage' && (!entry.stage || typeof entry.stage.stage !== 'string'
        || typeof entry.stage.timestamp !== 'string' || typeof entry.stage.status !== 'string'
        || typeof entry.stage.completed !== 'boolean'))
      || (entry.kind === 'action' && (!entry.action || typeof entry.action.action_type !== 'string'
        || typeof entry.action.timestamp !== 'string'))
    ) return false;
    const timestamp = Date.parse(entry.inclusion_timestamp);
    if (!Number.isFinite(timestamp) || entry.inclusion_timestamp.slice(0, 10) < ledger.coverage.start
      || entry.inclusion_timestamp.slice(0, 10) > ledger.coverage.end
      || entry.inclusion_timestamp < previousTimestamp
      || (entry.inclusion_timestamp === previousTimestamp && entry.sequence < previousSequence)) return false;
    ids.add(entry.entry_id);
    previousTimestamp = entry.inclusion_timestamp;
    previousSequence = entry.sequence;
  }
  trustedUsageTimelineLedgers.add(ledger);
  return true;
}

/**
 * A timeline ledger is atomic for cache/reuse purposes, but detailed-report
 * consumers retain the established one-row-per-workflow contract.  Keep the
 * source ID separately: `flow_id` remains a stable display key even for an
 * older/malformed entry that has no mutable persisted workflow reference.
 */
export type TimelineDisplayRow = WorkflowSummaryRow & {
  actor_active_ms?: Record<string, number>;
  source_flow_id?: string;
  timeline_entry_ids: string[];
};

function timelineEntryStage(entry: UsageTimelineEntry): StageEntry {
  if (entry.kind === 'stage') return entry.stage;
  return {
    stage: 'Workflow activity',
    timestamp: entry.action.timestamp,
    status: 'recorded',
    completed: false,
  };
}

export function timelineEntriesToDisplayRows(entries: readonly UsageTimelineEntry[], start?: string, end?: string): TimelineDisplayRow[] {
  const rowsByWorkflow = new Map<string, TimelineDisplayRow>();
  const selectedProofs = new WeakMap<object, { userLastActive: Record<string, string> | undefined; authoritativeActors: Set<string>; activeMs: number | undefined; activeByActor: Record<string, number> | undefined }>();
  const canonicalProofs = new Map<string, { userLastActive: Record<string, string> | undefined; authoritativeActors: Set<string>; activeMs: number | undefined; activeByActor: Record<string, number> | undefined }>();
  const usersByWorkflow = new Map<string, Set<string>>();
  // Full envelope validation establishes timestamp/sequence order and the day
  // index preserves it, so a selected range must not clone and sort again.
  for (const entry of entries) {
    const candidateSourceFlowId = entry.workflow.source_flow_id?.trim() || entry.workflow.flow_id;
    const sourceFlowId = typeof candidateSourceFlowId === 'string' && candidateSourceFlowId.trim()
      ? candidateSourceFlowId.trim()
      : undefined;
    // A missing source reference must not join independent malformed entries.
    const workflowKey = sourceFlowId ? `source:${sourceFlowId}` : `entry:${entry.entry_id}`;
    const existing = rowsByWorkflow.get(workflowKey);
    const stage = timelineEntryStage(entry);
    const withinSelection = (timestamp: string): boolean =>
      (!start || timestamp.slice(0, 10) >= start) && (!end || timestamp.slice(0, 10) <= end);
    const proofKey = entry.workflow;
    const canonicalRef = entry.workflow.duration_evidence_ref;
    let proof = canonicalRef === undefined ? selectedProofs.get(proofKey) : canonicalProofs.get(canonicalRef);
    if (!proof) {
      const activeByActor = entry.workflow.active_ms_by_day_and_user === undefined ? undefined : {} as Record<string, number>;
      if (activeByActor) {
        for (const [day, actors] of Object.entries(entry.workflow.active_ms_by_day_and_user!)) {
          if (!withinSelection(day)) continue;
          for (const [email, duration] of Object.entries(actors)) {
            activeByActor[email] = (activeByActor[email] ?? 0) + duration;
          }
        }
      }
      proof = {
        activeByActor,
        authoritativeActors: new Set(Object.entries(entry.workflow.user_last_active ?? {})
          .filter(([, timestamp]) => withinSelection(timestamp)).map(([email]) => email.trim().toLowerCase())),
        userLastActive: entry.workflow.user_last_active === undefined ? undefined
          : Object.fromEntries(Object.entries(entry.workflow.user_last_active)
            .filter(([, timestamp]) => withinSelection(timestamp))),
        activeMs: existing ? existing.active_ms : activeByActor === undefined
          ? entry.workflow.active_ms ?? undefined
          : Object.values(activeByActor).reduce((total, duration) => total + duration, 0),
      };
      selectedProofs.set(proofKey, proof);
      if (canonicalRef !== undefined) canonicalProofs.set(canonicalRef, proof);
    }
    // This range-local mutable buffer never modifies the canonical parent.
    const userLastActive = proof.userLastActive;
    // A cached wider envelope can carry an actor's latest action outside this
    // child range. Selected stages still provide that actor's own activity;
    // another collaborator's global workflow time is never a substitute.
    if (userLastActive && entry.kind === 'stage' && entry.stage.user) {
      const stageUser = entry.workflow.users.includes(entry.stage.user) ? entry.stage.user
        : entry.workflow.users.find(email => email.trim().toLowerCase() === entry.stage.user?.trim().toLowerCase());
      // A stage that starts in this child proves actor activity even when its
      // completion and the wider envelope's latest action fall on a later day.
      const timestamp = entry.stage.completed_at && withinSelection(entry.stage.completed_at)
        ? entry.stage.completed_at : entry.stage.timestamp;
      if (stageUser && !proof.authoritativeActors.has(stageUser.trim().toLowerCase())
        && withinSelection(timestamp) && (!userLastActive[stageUser]
        || timestamp > userLastActive[stageUser])) userLastActive[stageUser] = timestamp;
    }
    const selectedUsers = userLastActive === undefined || (!start && !end)
      ? entry.workflow.users
      : entry.workflow.users.filter(email => userLastActive[email] !== undefined);
    if (existing) {
      if (entry.workflow.is_training_example === true) {
        existing.is_training_example = true;
        existing.flow_name = `${entry.workflow.workflow_type || existing.workflow_type || 'Training'} example`;
      }
      // Keep one mutable construction buffer per workflow. Repeated spread
      // copies made a long timeline quadratic before any visible rows rendered.
      existing.timeline_entry_ids.push(entry.entry_id);
      existing.stages.push(stage);
      const users = usersByWorkflow.get(workflowKey)!;
      for (const email of selectedUsers) {
        if (!users.has(email)) { users.add(email); existing.users.push(email); }
      }
      existing.error_count += stage.status === 'error' ? 1 : 0;
      if (entry.inclusion_timestamp > existing.last_active) existing.last_active = entry.inclusion_timestamp;
      if (userLastActive) {
        existing.user_last_active ??= {};
        for (const [email, timestamp] of Object.entries(userLastActive)) {
          if (!existing.user_last_active[email] || timestamp > existing.user_last_active[email]) {
            existing.user_last_active[email] = timestamp;
          }
        }
      }
      continue;
    }
    usersByWorkflow.set(workflowKey, new Set(selectedUsers));
    rowsByWorkflow.set(workflowKey, {
      flow_id: sourceFlowId ?? entry.entry_id,
      source_flow_id: sourceFlowId,
      timeline_entry_ids: [entry.entry_id],
      flow_name: entry.workflow.is_training_example
        ? `${entry.workflow.workflow_type || 'Training'} example`
        : entry.workflow.flow_name ?? entry.workflow.flow_id,
      workflow_type: entry.workflow.workflow_type ?? null,
      is_training_example: entry.workflow.is_training_example,
      users: [...selectedUsers],
      total_requests: 0,
      last_active: entry.inclusion_timestamp,
      user_last_active: userLastActive,
      error_count: stage.status === 'error' ? 1 : 0,
      stages: [stage],
      errors: [],
      status: entry.workflow.status,
      // Repeated stage/action entries carry the same workflow evidence. Sum
      // it only when constructing the workflow, never once per display row.
      active_ms: proof.activeMs,
      actor_active_ms: proof.activeByActor,
    });
  }
  return Array.from(rowsByWorkflow.values());
}

export function formatUtcDate(date: Date): string {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** True only for a complete, real `YYYY-MM-DD` calendar date. Rejects empty /
 *  partial input and impossible dates (e.g. 2026-02-31). */
export function isValidDateStr(s: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const d = new Date(s + 'T00:00:00Z');
  return !isNaN(d.getTime()) && formatUtcDate(d) === s;
}
